import React, { useState } from 'react';
import { useAuth } from '../AuthContext';
import { useTranslation } from '../i18n/LanguageContext';
import { EnvelopeSimple, Lock, User, ArrowRight } from '@phosphor-icons/react';
import LanguageSwitcher from './LanguageSwitcher';

const ROLES = ['Executive', 'Operations', 'Sustainability', 'Finance'];

export default function LoginPage() {
  const { login, register } = useAuth();
  const { t } = useTranslation();
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('Operations');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = isRegister
      ? await register(email, password, name, role)
      : await login(email, password);
    if (!result.success) setError(result.error);
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1A2015] flex-col justify-between p-12">
        <div>
          <div className="flex items-center gap-3 mb-16">
            <img src="/logo-white.svg" alt="SusTek" className="w-10 h-10" />
            <span className="font-heading text-2xl font-bold text-white tracking-tight">SusTek</span>
            <span className="text-xs font-body text-tertiary uppercase tracking-[0.2em] ml-1">TM</span>
          </div>
          <h1 className="font-heading text-4xl font-extrabold text-white leading-tight mb-6 whitespace-pre-line">
            {t('login.heroTitle')}
          </h1>
          <p className="font-body text-lg text-[#A3A380] leading-relaxed max-w-md">
            {t('login.heroDescription')}
          </p>
        </div>
        <div className="flex items-center gap-8">
          {t('loginIndustries').map((ind) => (
            <span key={ind} className="text-xs font-body text-tertiary uppercase tracking-wider">{ind}</span>
          ))}
        </div>
      </div>

      {/* Right panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-background">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-10">
            <div className="lg:hidden flex items-center gap-3">
              <img src="/logo-colored.svg" alt="SusTek" className="w-8 h-8" />
              <span className="font-heading text-xl font-bold text-text-primary">SusTek</span>
            </div>
            <LanguageSwitcher variant="login" />
          </div>

          <h2 className="font-heading text-2xl font-bold text-text-primary mb-2">
            {isRegister ? t('login.createAccount') : t('login.welcomeBack')}
          </h2>
          <p className="font-body text-text-secondary mb-2">
            {isRegister ? t('login.registerSubtitle') : t('login.signInTitle')}
          </p>
          {!isRegister && (
            <p className="font-body text-xs text-tertiary mb-8" data-testid="login-description">
              {t('login.signInDescription')}
            </p>
          )}
          {isRegister && <div className="mb-8" />}

          {error && (
            <div className="bg-alert-critical/10 border border-alert-critical/30 text-alert-critical text-sm rounded-lg p-3 mb-6" data-testid="auth-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {isRegister && (
              <div>
                <label className="text-xs font-body font-semibold uppercase tracking-[0.15em] text-tertiary mb-2 block">{t('login.fullName')}</label>
                <div className="relative">
                  <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-tertiary" />
                  <input
                    data-testid="register-name-input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-surface border border-border rounded-lg font-body text-sm text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                    placeholder={t('login.namePlaceholder')}
                    required
                  />
                </div>
              </div>
            )}

            <div>
              <label className="text-xs font-body font-semibold uppercase tracking-[0.15em] text-tertiary mb-2 block">{t('login.email')}</label>
              <div className="relative">
                <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-tertiary" />
                <input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-surface border border-border rounded-lg font-body text-sm text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                  placeholder={t('login.emailPlaceholder')}
                  required
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-body font-semibold uppercase tracking-[0.15em] text-tertiary mb-2 block">{t('login.password')}</label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-tertiary" />
                <input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-surface border border-border rounded-lg font-body text-sm text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                  placeholder={t('login.passwordPlaceholder')}
                  required
                />
              </div>
            </div>

            {isRegister && (
              <div>
                <label className="text-xs font-body font-semibold uppercase tracking-[0.15em] text-tertiary mb-2 block">{t('login.role')}</label>
                <select
                  data-testid="register-role-select"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full px-4 py-3 bg-surface border border-border rounded-lg font-body text-sm text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                >
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-primary text-white py-3 rounded-lg font-heading font-semibold text-sm hover:bg-[#2B3523] transition-all duration-200 disabled:opacity-50"
            >
              {loading ? t('login.pleaseWait') : (isRegister ? t('login.create') : t('login.signIn'))}
              {!loading && <ArrowRight size={16} weight="bold" />}
            </button>
          </form>

          <p className="mt-6 text-center font-body text-sm text-text-secondary">
            {isRegister ? t('login.hasAccount') : t('login.noAccount')}{' '}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => { setIsRegister(!isRegister); setError(''); }}
              className="text-primary font-semibold hover:underline"
            >
              {isRegister ? t('login.signIn') : t('login.createAccount')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
